import { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface ExclusionSettingsProps {
  projectId: string;
  onClose: () => void;
}

const ALWAYS_EXCLUDED = [
  'node_modules/', '.venv/', 'venv/', '__pycache__/', 'vendor/', '.m2/', '.bundle/',
  'dist/', 'build/', '.next/', '.nuxt/', 'out/', '.cache/', '.turbo/', 'coverage/', 'target/',
];

export function ExclusionSettings({ projectId, onClose }: ExclusionSettingsProps) {
  const [patterns, setPatterns] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.getProject(projectId).then(project => {
      setPatterns((project.excludePatterns ?? []).join('\n'));
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, [projectId]);

  async function handleSave() {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const parsed = patterns
        .split('\n')
        .map(p => p.trim())
        .filter(p => p.length > 0);
      await api.updateProject(projectId, { excludePatterns: parsed });
      setSaveMessage({ type: 'success', text: 'Exclusion patterns saved.' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">File Exclusions</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Glob patterns to exclude from all analysis runs
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-4">
          {/* Always-excluded notice */}
          <div className="bg-muted/50 rounded-lg px-4 py-3 text-sm">
            <p className="font-medium text-foreground mb-1.5">Always excluded (built-in)</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              {ALWAYS_EXCLUDED.join(' · ')}
            </p>
          </div>

          {/* User patterns */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="exclude-patterns">
              Additional exclusions <span className="text-muted-foreground font-normal">(one glob pattern per line)</span>
            </label>
            {isLoading ? (
              <div className="h-36 rounded-md border border-border bg-muted/30 animate-pulse" />
            ) : (
              <textarea
                id="exclude-patterns"
                value={patterns}
                onChange={e => setPatterns(e.target.value)}
                placeholder={'src/legacy/**\nsrc/mocks/**\nsrc/generated/**'}
                rows={6}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                spellCheck={false}
              />
            )}
            <p className="text-xs text-muted-foreground">
              Uses glob syntax — e.g. <code className="bg-muted px-1 rounded">src/legacy/**</code>, <code className="bg-muted px-1 rounded">**/*.generated.ts</code>
            </p>
          </div>

          {saveMessage && (
            <p className={`text-sm ${saveMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {saveMessage.text}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
